import {
  DecisionPageLayout,
  DecisionSection,
  DecisionSubHeading,
  DecisionList,
  InlineRelatedLinks,
} from "@/components/DecisionPageLayout";
import { Link } from "@/lib/router-compat";

const seo = {
  title: "Federal Employee TMS Therapy | Neuro Wellness TMS",
  description:
    "Federal employees: learn about TMS therapy for depression through your FEHB or PSHB plan. Coverage may be available when medical necessity criteria are met.",
  canonical: "/federal-employee-tms",
  keywords:
    "federal employee TMS, FEHB TMS, federal worker TMS therapy, government employee depression treatment",
};

const relatedLinks = [
  { title: "Federal Employee Hub", href: "/federal-employee-mental-health" },
  { title: "FEHB TMS Coverage", href: "/fehb-tms-coverage" },
  { title: "Federal Employee SAINT TMS", href: "/federal-employee-saint-tms" },
  {
    title: "Federal Employee Insurance Verification",
    href: "/federal-employee-insurance-verification",
  },
  {
    title: "Does Federal BCBS Cover SAINT?",
    href: "/does-federal-bcbs-cover-saint",
  },
];

export default function FederalEmployeeTms() {
  return (
    <DecisionPageLayout
      seo={seo}
      badge="Federal Employee TMS"
      title="TMS Therapy for Federal Employees"
      subtitle="If you're a federal employee or annuitant with depression that hasn't responded to medication, TMS may be an option worth exploring through your health plan."
    >
      <DecisionSection heading="What TMS is">
        <p>
          Transcranial Magnetic Stimulation (TMS) is a non-invasive, FDA-cleared
          treatment that uses magnetic pulses to stimulate specific areas of the
          brain involved in mood regulation. It's commonly used for adults with
          treatment-resistant depression — depression that hasn't improved
          adequately with oral antidepressants.
        </p>
        <p>
          TMS is not ECT. There is no anesthesia, no induced seizure, and no
          memory loss. You sit in a chair, stay awake, and can drive yourself
          home after a standard session.
        </p>
      </DecisionSection>

      <DecisionSection heading="How TMS may be covered under FEHB">
        <p>
          TMS may be covered under your FEHB plan when medical necessity
          criteria are met. The specific requirements depend on your carrier and
          plan. Generally, plans look for:
        </p>
        <DecisionList
          items={[
            "A confirmed diagnosis of major depressive disorder",
            "Inadequate response to two or more antidepressants from different classes",
            "A documented trial of psychotherapy",
            "Medical necessity documentation from your provider",
          ]}
        />
        <p>
          Prior authorization may be required. Our team handles the
          verification, documentation, and authorization process on your behalf.
        </p>
      </DecisionSection>

      <DecisionSection heading="What a standard course looks like">
        <p>
          Standard TMS typically involves sessions five days a week for about
          six weeks. Each session lasts roughly 20 to 40 minutes. You can drive
          yourself to and from appointments.
        </p>
        <p>
          Accelerated protocols like SAINT compress this timeline significantly
          — but SAINT has its own eligibility and coverage considerations.{" "}
          <Link
            to="/federal-employee-saint-tms"
            className="text-accent hover:underline"
          >
            Learn about SAINT for federal employees
          </Link>
          .
        </p>
      </DecisionSection>

      <DecisionSection heading="Your next step">
        <p>
          The simplest next step is a phone consultation. We'll review your
          situation, explain what your plan may cover, and help you understand
          whether TMS is worth pursuing. There's no commitment.
        </p>
        <p>
          Call us at{" "}
          <a
            href="tel:9548279793"
            className="text-accent font-semibold hover:underline"
          >
            (954) 827-9793
          </a>{" "}
          or{" "}
          <Link
            to="/federal-employee-insurance-verification"
            className="text-accent hover:underline"
          >
            start the insurance verification process
          </Link>
          .
        </p>
      </DecisionSection>

      <InlineRelatedLinks
        heading="Related Federal Employee Pages"
        links={relatedLinks}
      />
    </DecisionPageLayout>
  );
}
